import { getGoogleMapsApiKey } from '@rsos/utils/metaTags';

// Cache for elevation data to avoid repeated API calls for the same location
const elevationCache = new Map<string, number>();

// Promise to track Google Maps loading state
let googleMapsLoadPromise: Promise<void> | null = null;

/**
 * Dynamically load Google Maps JavaScript API if not already loaded.
 * Returns a promise that resolves when the API is ready.
 */
const ensureGoogleMapsLoaded = async (): Promise<boolean> => {
  // Check if already loaded
  const googleMaps = (
    window as unknown as { google?: { maps?: { ElevationService?: unknown } } }
  ).google?.maps;
  if (googleMaps?.ElevationService) {
    return true;
  }

  // If already loading, wait for it
  if (googleMapsLoadPromise) {
    await googleMapsLoadPromise;
    return !!(
      window as unknown as {
        google?: { maps?: { ElevationService?: unknown } };
      }
    ).google?.maps?.ElevationService;
  }

  // Load Google Maps API dynamically
  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) {
    return false;
  }

  googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
    // Check if script already exists
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com"]',
    );
    if (existingScript) {
      // Script exists but API not ready yet, wait for it
      const checkInterval = setInterval(() => {
        if (
          (
            window as unknown as {
              google?: { maps?: { ElevationService?: unknown } };
            }
          ).google?.maps?.ElevationService
        ) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error('Timeout waiting for Google Maps API'));
      }, 10000);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps API'));
    document.head.appendChild(script);
  });

  try {
    await googleMapsLoadPromise;
    // Wait a bit for the API to initialize
    await new Promise(resolve => setTimeout(resolve, 100));
    return !!(
      window as unknown as {
        google?: { maps?: { ElevationService?: unknown } };
      }
    ).google?.maps?.ElevationService;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[ElevationService] Failed to load Google Maps API:', error);
    return false;
  }
};

/**
 * Get ground elevation for a location using Google Maps JavaScript API ElevationService.
 * Results are cached to avoid repeated API calls.
 * Will dynamically load Google Maps API if not already loaded.
 *
 * :param latitude: Location latitude
 * :param longitude: Location longitude
 * :return: Ground elevation in meters (MSL), or null if the request fails
 */
export const getGroundElevation = async (
  latitude: number,
  longitude: number,
): Promise<number | null> => {
  // Create a cache key with rounded coordinates (6 decimal places ~0.1m precision)
  const cacheKey = `${latitude.toFixed(6)},${longitude.toFixed(6)}`;

  // Check cache first
  if (elevationCache.has(cacheKey)) {
    return elevationCache.get(cacheKey) ?? null;
  }

  // Ensure Google Maps is loaded
  const isLoaded = await ensureGoogleMapsLoaded();
  if (!isLoaded) {
    return null;
  }

  // Use Google Maps JavaScript API ElevationService (handles CORS properly)
  const googleMaps = (
    window as unknown as {
      google?: {
        maps?: {
          ElevationService?: new () => {
            getElevationForLocations: (
              request: { locations: { lat: number; lng: number }[] },
              callback: (
                results: { elevation: number }[] | null,
                status: string,
              ) => void,
            ) => void;
          };
        };
      };
    }
  ).google?.maps;

  if (!googleMaps?.ElevationService) {
    return null;
  }

  try {
    const elevator = new googleMaps.ElevationService();

    return new Promise(resolve => {
      elevator.getElevationForLocations(
        { locations: [{ lat: latitude, lng: longitude }] },
        (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const elevation = results[0].elevation;
            // Cache the result
            elevationCache.set(cacheKey, elevation);
            resolve(elevation);
          } else {
            resolve(null);
          }
        },
      );
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[ElevationService] Failed to get ground elevation:', error);
    return null;
  }
};
